import { motion } from "framer-motion";

/**
 * PageHero — consistent editorial hero for every internal page.
 * Full-viewport image with vignette + editorial title stack over it.
 */
export default function PageHero({ eyebrow, title, subtitle, image, kicker, testid }) {
  return (
    <section
      className="relative w-full flex items-end overflow-hidden"
      style={{ minHeight: "78vh", background: "var(--bg-2)" }}
      data-testid={testid || "page-hero"}
    >
      {image && (
        <motion.img
          src={image}
          alt=""
          data-sound-pulse
          className="absolute inset-0 w-full h-full object-cover"
          initial={{ scale: 1.12, opacity: 0 }}
          animate={{ scale: 1.02, opacity: 1 }}
          transition={{ duration: 2.4, ease: [0.16, 1, 0.3, 1] }}
          draggable={false}
        />
      )}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0.9) 100%)",
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 20% 100%, rgba(198,168,124,0.14), transparent 55%)",
        }}
      />

      <div className="relative z-10 w-full max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14 pb-20 md:pb-28 pt-40">
        {eyebrow && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.44em]"
            style={{ color: "var(--gold)" }}
          >
            <span
              className="inline-block w-1.5 h-1.5 rounded-full"
              style={{ background: "var(--gold)", boxShadow: "0 0 12px var(--gold)" }}
            />
            {eyebrow}
          </motion.div>
        )}
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 font-display leading-[0.92]"
          style={{ fontSize: "clamp(3rem, 8vw, 8rem)", letterSpacing: "-0.035em" }}
        >
          {title}
        </motion.h1>
        {subtitle && (
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.35 }}
            className="mt-8 max-w-2xl font-body text-lg md:text-xl"
            style={{ color: "var(--text-2)", lineHeight: 1.7 }}
          >
            {subtitle}
          </motion.p>
        )}
        {kicker && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.55, duration: 1 }}
            className="mt-10 pt-6 max-w-md font-mono text-[10px] uppercase tracking-[0.4em]"
            style={{ color: "var(--text-3)", borderTop: "1px solid var(--line)" }}
          >
            {kicker}
          </motion.div>
        )}
      </div>
    </section>
  );
}
